import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { ThemeProvider as AuthMuiThemeProvider } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { getCookie, getLoginSecured } from "../../utils/api";
import { useTheme } from "../../context/ThemeContext";
import { getAppBrand } from "../../config/resolve-app-brand";
import {
  createRegisterPageDarkTheme,
  createRegisterPageLightTheme,
} from "../../theme/registerPageTheme";
import { useSnackbar } from "notistack";
import { clearAllCookies } from "../../utils/cookie-utils";
import { useNavigate } from "react-router-dom";
import envConfig from "../../config";
import { performQuickLogout } from "../../utils/logout-utils";
import { isPlaceholderApiKeyExpiration } from "../../utils/api-key-expiry";
import {
  authxAppsBase,
  authxSessionHeaders,
  buildAuthxSessionGetUrl,
  getLoginVerificationRedirect,
  isAuthxLoginSessionUsable,
  loginAuthxHeadersPreSession,
  normalizeAuthxSession,
  mergeAuthxVerifyMfaSession,
  isVerifyMfaResponseSuccess,
  persistAuthxSessionCookies,
  persistPendingVerificationState,
  clearPendingVerificationState,
  shouldActivateCreatedUser,
  activateCreatedUser,
} from "../../utils/authx-login-helpers";
import {
  getAuthxAppsErrorMessage,
  isAuthxAppsOperationSuccess,
  parseAuthxAppsResponseBody,
  isAuthxMfaNotEnabledBody,
  isAuthxSessionExpiredBody,
} from "../../utils/authx-apps-response";
import { notifyLoginComplete } from "../../lib/api-interceptor";
import ThemeToggle from "../ThemeToggle/ThemeToggle";

import "../../styles/register-exacc.css";

const LoginPopup = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [loginEmail, setLoginEmail] = useState();
  const [smsSent, setSmsSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const interimAuthRef = useRef({ accessKey: "", refreshKey: "" });

  const { isDarkMode } = useTheme();
  const appBrand = useMemo(() => getAppBrand(), []);
  const authTheme = useMemo(
    () =>
      isDarkMode
        ? createRegisterPageDarkTheme(appBrand)
        : createRegisterPageLightTheme(appBrand),
    [appBrand, isDarkMode]
  );

  const redirectToLogin = async () => {
    performQuickLogout();
    await new Promise((resolve) => setTimeout(resolve, 500));
    window.location = `https://${envConfig.loginUrl}/`;
  };

  const runAuthxLoginAndMfa = async () => {
    const loginSecured = await getLoginSecured(email, password);
    if (!loginSecured) {
      enqueueSnackbar("Login credentials are incomplete", { variant: "error" });
      return false;
    }

    const loginRes = await fetch(`${authxAppsBase()}/login`, {
      method: "POST",
      headers: loginAuthxHeadersPreSession("127.0.0.2"),
      body: JSON.stringify({ loginHash: loginSecured }),
    });
    const loginBody = await parseAuthxAppsResponseBody(loginRes);
    const loginRaw =
      typeof loginBody === "object" && loginBody ? loginBody : {};
    const loginPayload = normalizeAuthxSession(loginRaw);
    const verificationRedirect = getLoginVerificationRedirect(loginPayload);

    interimAuthRef.current = {
      accessKey: loginPayload.accessKey ?? "",
      refreshKey: loginPayload.refreshKey ?? "",
    };

    if (
      !isAuthxLoginSessionUsable(
        loginRes.ok,
        loginPayload,
        verificationRedirect,
      )
    ) {
      enqueueSnackbar(getAuthxAppsErrorMessage(loginBody, "Login failed"), {
        variant: "error",
      });
      interimAuthRef.current = { accessKey: "", refreshKey: "" };
      return false;
    }

    if (verificationRedirect === "email") {
      persistAuthxSessionCookies(loginRaw, email);
      persistPendingVerificationState(loginPayload, loginSecured);
      setPassword("");
      setCode("");
      handleClose();
      navigate("/verify-email", {
        state: {
          firstName: loginPayload.firstName,
          email,
          loginSecured,
          accessKey: loginPayload.accessKey,
          refreshKey: loginPayload.refreshKey,
        },
      });
      return false;
    }

    if (verificationRedirect === "phone") {
      persistAuthxSessionCookies(loginRaw, email);
      persistPendingVerificationState(loginPayload, loginSecured);
      setPassword("");
      setCode("");
      handleClose();
      navigate("/verify-phone", {
        state: {
          loginSecuredHash: loginSecured,
          phone: loginPayload.mobPhoneNo,
          accessKey: loginPayload.accessKey,
          refreshKey: loginPayload.refreshKey,
        },
      });
      return false;
    }

    const verifyRes = await fetch(`${authxAppsBase()}/verify-mfa`, {
      method: "POST",
      headers: authxSessionHeaders(
        loginPayload.accessKey,
        loginPayload.refreshKey,
        "127.0.0.2",
      ),
      body: JSON.stringify({
        accessKey: loginPayload.accessKey,
        mfaCode: code,
        rememberMe: remember ? "Y" : "N",
      }),
    });

    const verificationBody = await parseAuthxAppsResponseBody(verifyRes);

    if (isAuthxMfaNotEnabledBody(verificationBody)) {
      persistAuthxSessionCookies(
        {
          accessKey: loginPayload.accessKey,
          refreshKey: loginPayload.refreshKey,
        },
        email,
      );
      setPassword("");
      setCode("");
      handleClose();
      navigate("/mfa-setup", {
        state: {
          accessKey: loginPayload.accessKey,
          refreshKey: loginPayload.refreshKey,
        },
      });
      return false;
    }

    if (isAuthxSessionExpiredBody(verificationBody)) {
      clearAllCookies();
      interimAuthRef.current = { accessKey: "", refreshKey: "" };
      setPassword("");
      setCode("");
      handleClose();
      navigate("/");
      return false;
    }

    const verificationRaw =
      typeof verificationBody === "object" && verificationBody
        ? verificationBody
        : {};
    const merged = mergeAuthxVerifyMfaSession(loginPayload, verificationRaw);

    const verifyOk = isVerifyMfaResponseSuccess(
      verifyRes.ok,
      verificationRaw,
      merged,
    );

    if (!verifyOk) {
      enqueueSnackbar(
        getAuthxAppsErrorMessage(verificationBody, "Verification failed"),
        { variant: "error" },
      );
      interimAuthRef.current = { accessKey: "", refreshKey: "" };
      return false;
    }

    persistAuthxSessionCookies(
      {
        ...verificationRaw,
        accessKey: merged.accessKey,
        refreshKey: merged.refreshKey,
      },
      email,
    );
    clearPendingVerificationState();

    if (shouldActivateCreatedUser(merged)) {
      const loginSecuredHash = await getLoginSecured(email, password);
      const activation = await activateCreatedUser(loginSecuredHash);
      if (!activation.ok) {
        enqueueSnackbar(
          activation.errorMessage ||
            "Account activation failed. Please try again.",
          { variant: "error" },
        );
        interimAuthRef.current = { accessKey: "", refreshKey: "" };
        return false;
      }
    }

    setPassword("");
    setCode("");
    handleClose();
    notifyLoginComplete(true);
    if (window.location.href.includes("user-dashboard")) {
      navigate(0);
    }
    return true;
  };

  const handleLogin = async () => {
    const storedTime = getCookie("apiKeyExpirationUTCDate");
    const accessKeyCookie = getCookie("accessKey");
    const refreshKeyCookie = getCookie("refreshKey");

    if (
      (!accessKeyCookie ||
        accessKeyCookie === "undefined" ||
        !refreshKeyCookie ||
        refreshKeyCookie === "undefined") &&
      (!storedTime || isPlaceholderApiKeyExpiration(storedTime))
    ) {
      window.location = `https://${envConfig.loginUrl}/`;
      return;
    }

    await runAuthxLoginAndMfa();
  };

  const sendCodeViaSMS = async () => {
    const accessKeyCandidate =
      interimAuthRef.current.accessKey || getCookie("accessKey");
    const refreshKeyCandidate =
      interimAuthRef.current.refreshKey || getCookie("refreshKey");

    if (
      !refreshKeyCandidate ||
      refreshKeyCandidate === "undefined" ||
      !accessKeyCandidate ||
      accessKeyCandidate === "undefined"
    ) {
      enqueueSnackbar("Cannot send SMS. Please log back in...", {
        variant: "warning",
      });
      clearAllCookies();
      window.location.href = "/";
      return;
    }

    setSmsSent(true);
    const smsUrl = buildAuthxSessionGetUrl(
      "send-2fa-code-as-sms",
      accessKeyCandidate,
      refreshKeyCandidate,
    );

    try {
      const smsResponse = await fetch(smsUrl, {
        method: "GET",
        headers: authxSessionHeaders(
          accessKeyCandidate,
          refreshKeyCandidate,
          "115.70.37.103",
        ),
      });
      const smsJson = await smsResponse.json().catch(() => ({}));
      if (isAuthxAppsOperationSuccess(smsResponse.ok, smsJson)) {
        enqueueSnackbar("SMS sent successfully", { variant: "success" });
      } else {
        setSmsSent(false);
        enqueueSnackbar(
          getAuthxAppsErrorMessage(
            smsJson,
            "Failed to send SMS code. Please try again.",
          ),
          { variant: "error" },
        );
      }
    } catch {
      setSmsSent(false);
    }
  };

  useEffect(() => {
    const em = getCookie("loginEmail");
    setEmail(em);
    setLoginEmail(em);
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key === "r") {
        setRemember((prevRemember) => !prevRemember);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const themeMode = isDarkMode ? "dark" : "light";

  return (
    <AuthMuiThemeProvider theme={authTheme}>
      <Dialog
        className="login-popup-dialog"
        data-theme={themeMode}
        fullWidth
        maxWidth="xs"
        open={open}
        onClose={async (_, reason) => {
          if (reason && reason === "backdropClick") return;
          await redirectToLogin();
        }}
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
            maxWidth: 460,
            width: "100%",
            m: 2,
          },
        }}
        data-testid="login-popup--Dialog-0"
      >
        <Box
          className="register-exacc-scope"
          data-theme={themeMode}
          sx={{ width: "100%" }}
        >
          <Box className="glass-card" sx={{ p: { xs: 3, sm: 3.5, md: 4 }, width: "100%" }}>
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
              Session expired
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              Sign in again to continue working in your Exacc workspace.
            </Typography>

            <TextField
              autoFocus
              label="Email Address"
              type="email"
              fullWidth
              value={email}
              disabled={Boolean(loginEmail)}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2.5 }}
              data-testid="login-popup--TextField-0"
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2.5 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "text.secondary" }}
                      data-testid="login-popup--IconButton-0"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              data-testid="login-popup--TextField-1"
            />

            <TextField
              label="2FA Code"
              type="tel"
              fullWidth
              value={code}
              onChange={(e) => {
                const newValue = e.target.value;
                if (/^\d*$/.test(newValue) && newValue.length <= 6) {
                  setCode(newValue);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && code.length === 6 && password) {
                  handleLogin();
                }
              }}
              inputProps={{
                style: {
                  textAlign: "center",
                  fontSize: "16px",
                  letterSpacing: "5px",
                },
              }}
              sx={{
                mb: 2,
                "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              data-testid="login-popup--TextField-2"
            />

            <FormControlLabel
              sx={{ mb: 2, span: { fontSize: 14 } }}
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-popup--Checkbox-0"
                />
              }
              label={
                <span>
                  <u>R</u>emember Me
                </span>
              }
              data-testid="login-popup--FormControlLabel-0"
            />

            <Button
              variant="outlined"
              className="btn-glass"
              fullWidth
              disabled={code.length !== 6 || !password}
              onClick={handleLogin}
              sx={{ mb: 3 }}
              data-testid="login-popup--Button-1"
            >
              Log In
            </Button>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 3,
              }}
              data-testid="login-popup--DialogActions-0"
            >
              <Link
                component="button"
                type="button"
                onClick={() => {
                  if (!smsSent) sendCodeViaSMS();
                }}
                aria-disabled={smsSent}
                sx={{
                  color: smsSent ? "text.disabled" : "primary.main",
                  cursor: smsSent ? "default" : "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  background: "none",
                  border: "none",
                  p: 0,
                }}
                data-testid="login-popup--Link-0"
              >
                Get 2FA code via SMS
              </Link>
              <Link
                component="button"
                type="button"
                onClick={redirectToLogin}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  background: "none",
                  border: "none",
                  p: 0,
                }}
                data-testid="login-popup--Button-0"
              >
                Cancel
              </Link>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ThemeToggle
                titlefontSize={16}
                title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              />
            </Box>
          </Box>
        </Box>
      </Dialog>
    </AuthMuiThemeProvider>
  );
};

export default LoginPopup;
